import { Stack } from "@mui/material";
import React from "react";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { CollectionGroupType } from "../../../../common/service/server/controller/CollectionGroupsController";
import { CollectionContext } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { GetCompliantPageWrapper } from "../../../components/layout/GetCompliantPageWrapper";
import { PageHeader } from "../../../components/layout/header/Header";
import {
  FcraCallout,
  GlbaCallout,
  HipaaCallout,
  IncludeInformationCollectedForOtherBusinessesCallout,
} from "../../../components/organisms/callouts/RegulationCallouts";
import { DataRecipientDisclosureCards } from "../../../components/organisms/DataRecipientCollectionCards";
import { useCollectionGroups } from "../../../hooks/useCollectionGroups";
import { useDisclosuresForCollectionGroups } from "../../../hooks/useDisclosuresForCollectionGroups";
import { usePrimaryOrganizationId } from "../../../hooks/useOrganization";
import {
  BUSINESS_SURVEY_NAME,
  COLLECTION_FOR_OTHER_BUSINESSES_SLUG,
} from "../../../surveys/businessSurvey";
import { TodoListCallout } from "../../../components/organisms/TodoListCallout";

const EXCLUDE_COLLECTION_GROUP_TYPE: CollectionGroupType[] = ["EMPLOYMENT"];
const CONSUMER_CONTEXT: CollectionContext[] = ["CONSUMER"];

export const ConsumerDataDisclosuresPage: React.FC = () => {
  const orgId = usePrimaryOrganizationId();

  const [groups, groupsRequest] = useCollectionGroups(
    orgId,
    undefined,
    EXCLUDE_COLLECTION_GROUP_TYPE,
  );

  const { data: dataMap } = useDataMap(orgId);
  const disclosureData = useDisclosuresForCollectionGroups(orgId, groups, CONSUMER_CONTEXT);
  const { answers, request: surveyRequest } = useSurvey(orgId, BUSINESS_SURVEY_NAME);

  return (
    <GetCompliantPageWrapper
      requests={[
        groupsRequest,
        surveyRequest,
        disclosureData.requests.dataMapRequest,
        disclosureData.requests.recipientsRequest,
      ]}
    >
      <PageHeader
        titleContent="Disclosures"
        descriptionContent={
          <>
            <h4 className="intermediate">
              What categories of information are collected, analyzed by, stored in, or otherwise
              processed by your Data Recipients?
            </h4>

            <p>
              For each of your Data Recipients, identify which categories of personal data they
              process. This includes any categories of digital information that may be collected and
              processed automatically, as well as any information that your Vendors or Agencies have
              access to view.
            </p>
          </>
        }
      />

      <Stack spacing={2} className="mb-lg">
        {answers["business-hipaa-compliant"] === "true" && <HipaaCallout />}
        {answers["business-glba-compliant"] === "true" && <GlbaCallout />}
        {answers["business-fcra-compliant"] === "true" && <FcraCallout />}
        {answers[COLLECTION_FOR_OTHER_BUSINESSES_SLUG] === "true" && (
          <IncludeInformationCollectedForOtherBusinessesCallout />
        )}
      </Stack>

      <Stack spacing={3}>
        <DataRecipientDisclosureCards
          dataMap={dataMap}
          disclosureData={disclosureData}
          groups={groups}
          context={["CONSUMER"]}
          showPendingRecipients={true}
        />
        <TodoListCallout groups={disclosureData.unmappedDisclosurePICGroups} />
      </Stack>
    </GetCompliantPageWrapper>
  );
};
